import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AIAssistant from './AIAssistant';
import CreateAdmin from './CreateAdmin';
import LiveMap from './LiveMap';
import Settings from './Settings';

const API = 'https://weridesudan.com/api/admin';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTs = (ts?: string | null) =>
  ts ? new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  }) : '–';

const starRating = (val: number | null | undefined) =>
  val != null ? `⭐ ${Number(val).toFixed(1)}` : '—';

// ─── Shared overlay ───────────────────────────────────────────────────────────

function Overlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = 'Delete', confirmColor = '#e53935' }: {
  message: string; onConfirm: () => void; onCancel: () => void;
  confirmLabel?: string; confirmColor?: string;
}) {
  return (
    <Overlay onClose={onCancel}>
      <h3 style={styles.modalTitle}>Confirm</h3>
      <p style={{ color: '#444', margin: '0 0 20px' }}>{message}</p>
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onCancel}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: confirmColor }} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </Overlay>
  );
}

// ─── Add user modal ────────────────────────────────────────────────────────────

type UserRole = 'driver' | 'rider' | 'dispatcher';

function AddUserModal({ role, token, onClose, onDone }: {
  role: UserRole; token: string; onClose: () => void; onDone: () => void;
}) {
  const isDispatcher = role === 'dispatcher';
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: isDispatcher ? '' : 'WeRide123!' });
  const [err, setErr] = useState('');
  const h = { Authorization: `Bearer ${token}` };
  const label = role === 'driver' ? 'Driver' : role === 'rider' ? 'Rider' : 'Dispatcher';
  const fields: (keyof typeof form)[] = isDispatcher
    ? ['name', 'phone', 'email', 'password']
    : ['name', 'phone', 'email'];

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { setErr('Name and phone are required.'); return; }
    if (isDispatcher && !form.password.trim()) { setErr('Password is required.'); return; }
    try {
      await axios.post(`${API}/users`, { ...form, role }, { headers: h });
      onDone(); onClose();
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Failed to create user.');
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>Add New {label}</h3>
      {fields.map(f => (
        <input key={f} type={f === 'password' ? 'password' : 'text'} style={styles.formInput}
          placeholder={f.charAt(0).toUpperCase() + f.slice(1)} value={form[f]}
          onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))} />
      ))}
      {err && <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 8px' }}>{err}</p>}
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={submit}>Create {label}</button>
      </div>
    </Overlay>
  );
}

// ─── Profile modal ─────────────────────────────────────────────────────────────

function ProfileModal({ user, onClose }: { user: any; onClose: () => void }) {
  const rawStatus: string = user.status || (user.isActive ? 'active' : 'suspended');
  const statusColor: Record<string, string> = { pending: '#f59e0b', active: '#4caf50', suspended: '#f97316', rejected: '#e53935' };

  const rows: [string, React.ReactNode][] = [
    ['Role', user.role?.toUpperCase() || '–'],
    ['Phone', user.phone || '–'],
    ['Email', user.email || '–'],
    ['Status', <span style={{ ...styles.badge, backgroundColor: statusColor[rawStatus] ?? '#9ca3af' }}>
      {rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1)}
    </span>],
    ['Online', user.isOnline ? '🟢 Online' : '🔴 Offline'],
    ['Avg Rating', user.rating ? `⭐ ${Number(user.rating).toFixed(2)}` : '–'],
    ['Total Rides', user.totalRides ?? '–'],
    ['Signed Up', formatTs(user.createdAt)],
  ];

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>{user.name}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '9px 4px', color: '#888', width: 110 }}>{k}</td>
              <td style={{ padding: '9px 4px', fontWeight: 500, color: '#111' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: 'right', marginTop: 18 }}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ─── Contact modal ─────────────────────────────────────────────────────────────

interface ContactEntry { label: string; name: string; phone: string }

function ContactModal({ title, contacts, onClose }: { title: string; contacts: ContactEntry[]; onClose: () => void }) {
  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>{title}</h3>
      {contacts.map(c => (
        <div key={c.label} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{c.label}</div>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#111' }}>{c.name}</div>
          <div style={{ fontSize: 15, color: '#1a73e8', marginTop: 2 }}>📞 {c.phone}</div>
        </div>
      ))}
      {contacts.length === 0 && <p style={{ color: '#9ca3af' }}>No contact info available.</p>}
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────

function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data.token);
    } catch { setError('Invalid credentials'); }
  };

  return (
    <div style={styles.loginContainer}>
      <div style={styles.loginBox}>
        <h1 style={styles.logo}>🚗 We Ride Sudan</h1>
        <h2 style={styles.loginTitle}>Admin Dashboard</h2>
        <input style={styles.input} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
        <input style={styles.input} placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} />
        {error && <p style={styles.error}>{error}</p>}
        <button style={styles.button} onClick={handleLogin}>Login</button>
      </div>
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

type DriverFilter = 'all' | 'pending' | 'active' | 'suspended';

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState('rides');
  const [rides, setRides] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [dispatchLog, setDispatchLog] = useState<any[]>([]);
  const [driverFilter, setDriverFilter] = useState<DriverFilter>('all');

  const [addModalRole, setAddModalRole] = useState<UserRole | null>(null);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    message: string; onConfirm: () => void; confirmLabel?: string; confirmColor?: string;
  } | null>(null);
  const [selectedRideIds, setSelectedRideIds] = useState<Set<string>>(new Set());
  const [contactModal, setContactModal] = useState<{ title: string; contacts: ContactEntry[] } | null>(null);
  const [rideDriverMap, setRideDriverMap] = useState<Record<string, string>>({});

  const h = { Authorization: `Bearer ${token}` };

  const reloadUsers = async () => { const r = await axios.get(`${API}/users`, { headers: h }); setUsers(r.data.users || []); };
  const reloadRides = async () => { const r = await axios.get(`${API}/rides`, { headers: h }); setRides(r.data.rides || []); };
  const reloadDispatchLog = async () => {
    try { const r = await axios.get(`${API}/dispatch-log`, { headers: h }); setDispatchLog(r.data.logs || r.data || []); }
    catch { setDispatchLog([]); }
  };

  useEffect(() => {
    reloadRides(); reloadUsers(); reloadDispatchLog();
    axios.get(`${API}/stats`, { headers: h }).then(r => setStats(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Derived lists ─────────────────────────────────────────────────────────

  const driverStatus = (d: any): string => d.status || (d.isActive ? 'active' : 'suspended');

  const drivers     = users.filter(u => u.role === 'driver');
  const riders      = users.filter(u => u.role === 'rider');
  const dispatchers = users.filter(u => u.role === 'dispatcher');
  const availableDrivers = drivers.filter(d => d.isActive && (d.isAvailable || d.isOnline));
  const boardRides  = rides.filter(r => !['completed', 'cancelled'].includes(r.status));

  const filteredDrivers = driverFilter === 'all'
    ? drivers
    : drivers.filter(d => driverStatus(d) === driverFilter);

  // ── User actions ──────────────────────────────────────────────────────────

  const toggleUser = async (id: string, isActive: boolean) => {
    await axios.patch(`${API}/users/${id}`, { isActive: !isActive }, { headers: h });
    await reloadUsers();
  };

  const deleteUser = (id: string, name: string) => {
    setPendingConfirm({
      message: `Delete "${name}" permanently? This cannot be undone.`,
      onConfirm: async () => { await axios.delete(`${API}/users/${id}`, { headers: h }); await reloadUsers(); setPendingConfirm(null); },
    });
  };

  const approveDriver = async (id: string) => {
    await axios.patch(`${API}/drivers/${id}/approve`, {}, { headers: h });
    await reloadUsers();
  };

  const rejectDriver = (id: string, name: string) => {
    setPendingConfirm({
      message: `Reject "${name}"? Their account will be marked as rejected.`,
      confirmLabel: 'Reject',
      confirmColor: '#e53935',
      onConfirm: async () => {
        await axios.patch(`${API}/drivers/${id}/reject`, {}, { headers: h });
        await reloadUsers();
        setPendingConfirm(null);
      },
    });
  };

  // ── Ride selection ────────────────────────────────────────────────────────

  const toggleRide = (id: string) => {
    setSelectedRideIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };
  const toggleAllRides = () => {
    setSelectedRideIds(prev => prev.size === rides.length ? new Set() : new Set(rides.map(r => r._id)));
  };
  const deleteSelectedRides = () => {
    const count = selectedRideIds.size;
    setPendingConfirm({
      message: `Delete ${count} selected ride${count !== 1 ? 's' : ''} permanently?`,
      onConfirm: async () => {
        await Promise.all(Array.from(selectedRideIds).map(id => axios.delete(`${API}/rides/${id}`, { headers: h })));
        await reloadRides(); setSelectedRideIds(new Set()); setPendingConfirm(null);
      },
    });
  };

  // ── Dispatch board ────────────────────────────────────────────────────────

  const assignDriver = async (rideId: string) => {
    const driverId = rideDriverMap[rideId];
    if (!driverId) return;
    try {
      await axios.patch(`${API}/rides/${rideId}/assign`, { driverId }, { headers: h });
      await reloadRides(); await reloadDispatchLog();
      setRideDriverMap(prev => { const s = { ...prev }; delete s[rideId]; return s; });
    } catch (e: any) { alert(e.response?.data?.message || 'Assignment failed.'); }
  };

  const openContact = (ride: any) => {
    const contacts: ContactEntry[] = [];
    if (ride.rider?.name) contacts.push({ label: 'Rider', name: ride.rider.name, phone: ride.rider.phone || 'N/A' });
    if (ride.driver?.name) contacts.push({ label: 'Driver', name: ride.driver.name, phone: ride.driver.phone || 'N/A' });
    setContactModal({ title: 'Contact Info', contacts });
  };

  // ── Shared row helpers ────────────────────────────────────────────────────

  const clickableRow: React.CSSProperties = { cursor: 'pointer' };
  const hoverRow   = (e: React.MouseEvent<HTMLTableRowElement>) => (e.currentTarget.style.backgroundColor = '#f9fafb');
  const unhoverRow = (e: React.MouseEvent<HTMLTableRowElement>) => (e.currentTarget.style.backgroundColor = '');

  const statusBadge = (isActive: boolean) => (
    <span style={{ ...styles.badge, backgroundColor: isActive ? '#4caf50' : '#f97316' }}>
      {isActive ? 'Active' : 'Suspended'}
    </span>
  );

  // Driver-specific: three states with distinct colours
  const driverStatusBadge = (d: any) => {
    const s = driverStatus(d);
    const color = s === 'pending' ? '#f59e0b' : s === 'active' ? '#4caf50' : s === 'rejected' ? '#6b7280' : '#f97316';
    return <span style={{ ...styles.badge, backgroundColor: color }}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
  };

  const driverActionCell = (d: any) => {
    const s = driverStatus(d);
    return (
      <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, padding: '8px 12px', flexWrap: 'wrap' }}>
        {s === 'pending' ? (
          <>
            <button style={{ ...styles.actionBtn, backgroundColor: '#22c55e', fontSize: 12 }} onClick={() => approveDriver(d._id)}>Approve</button>
            <button style={{ ...styles.actionBtn, backgroundColor: '#e53935', fontSize: 12 }} onClick={() => rejectDriver(d._id, d.name)}>Reject</button>
          </>
        ) : (
          <>
            <button
              style={{ ...styles.actionBtn, backgroundColor: s === 'active' ? '#f59e0b' : '#43a047', fontSize: 12 }}
              onClick={() => toggleUser(d._id, d.isActive)}
            >
              {s === 'active' ? 'Suspend' : 'Activate'}
            </button>
            <button style={{ ...styles.actionBtn, backgroundColor: '#e53935', fontSize: 12 }} onClick={() => deleteUser(d._id, d.name)}>Delete</button>
          </>
        )}
      </td>
    );
  };

  const userActionCell = (u: any) => (
    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, padding: '8px 12px' }}>
      <button
        style={{ ...styles.actionBtn, backgroundColor: u.isActive ? '#f59e0b' : '#43a047', fontSize: 12 }}
        onClick={() => toggleUser(u._id, u.isActive)}
      >
        {u.isActive ? 'Suspend' : 'Activate'}
      </button>
      <button style={{ ...styles.actionBtn, backgroundColor: '#e53935', fontSize: 12 }} onClick={() => deleteUser(u._id, u.name)}>Delete</button>
    </td>
  );

  const tabs = ['rides','drivers','riders','dispatchers','dispatch-board','dispatch-log','stats','ai','live-map','settings','create-admin'];
  const tabLabel: Record<string, string> = {
    rides: '🚗 Rides', drivers: '🧑 Drivers', riders: '👤 Riders',
    dispatchers: '👨‍💼 Dispatchers', 'dispatch-board': '📋 Dispatch Board', 'dispatch-log': '📝 Activity Log',
    stats: '📊 Statistics', ai: '🤖 AI Assistant', 'live-map': '🗺 Live Map',
    settings: '⚙️ Settings', 'create-admin': '➕ Create Admin',
  };

  const pendingCount = drivers.filter(d => driverStatus(d) === 'pending').length;

  return (
    <div style={styles.container}>

      {/* ── Sidebar ── */}
      <div style={styles.sidebar}>
        <h2 style={styles.sidebarTitle}>🚗 WeRide Admin</h2>
        {tabs.map(t => (
          <div key={t} style={{ ...styles.navItem, ...(tab === t ? styles.navActive : {}), position: 'relative' }} onClick={() => setTab(t)}>
            {tabLabel[t]}
            {t === 'drivers' && pendingCount > 0 && (
              <span style={styles.navBadge}>{pendingCount}</span>
            )}
          </div>
        ))}
        <div style={{ marginTop: 'auto' }}>
          <div style={styles.navItem} onClick={onLogout}>🚪 Logout</div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={styles.main}>

        {/* STATS */}
        {tab === 'stats' && stats && (
          <div>
            <h2>📊 Overview</h2>
            <div style={styles.statsGrid}>
              {[
                ['Total Rides', stats.totalRides], ['Active Rides', stats.activeRides],
                ['Total Drivers', stats.totalDrivers], ['Total Riders', stats.totalRiders],
                ['Total Revenue', `${stats.totalRevenue} SDG`],
              ].map(([label, value]) => (
                <div key={label} style={styles.statCard}>
                  <div style={{ fontSize: 32, fontWeight: 'bold' }}>{value}</div>
                  <div>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIDES */}
        {tab === 'rides' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>🚗 All Rides</h2>
              {selectedRideIds.size > 0 && (
                <button style={{ ...styles.actionBtn, backgroundColor: '#e53935' }} onClick={deleteSelectedRides}>
                  Delete Selected ({selectedRideIds.size})
                </button>
              )}
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input type="checkbox" checked={rides.length > 0 && selectedRideIds.size === rides.length} onChange={toggleAllRides} />
                  </th>
                  <th>Rider</th><th>Driver</th><th>Pickup</th><th>Dropoff</th>
                  <th>Fare</th><th>Status</th><th>Ratings</th><th>Requested At</th>
                </tr>
              </thead>
              <tbody>
                {rides.map(r => (
                  <tr key={r._id}>
                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedRideIds.has(r._id)} onChange={() => toggleRide(r._id)} />
                    </td>
                    <td>{r.rider?.name || 'N/A'}</td>
                    <td>{r.driver?.name || 'Unassigned'}</td>
                    <td>{r.pickup?.address || '–'}</td>
                    <td>{r.destination?.address || '–'}</td>
                    <td>{r.fare?.estimated} SDG</td>
                    <td>
                      <span style={{ ...styles.badge, backgroundColor: r.status === 'completed' ? '#4caf50' : r.status === 'cancelled' ? '#f44336' : '#ff9800' }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, lineHeight: '1.6' }}>
                      {r.status === 'completed'
                        ? <><div>🚗 {starRating(r.driverRating ?? r.rating)}</div><div>👤 {starRating(r.riderRating)}</div></>
                        : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatTs(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DRIVERS */}
        {tab === 'drivers' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>🧑 Drivers</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={() => setAddModalRole('driver')}>
                + Add New Driver
              </button>
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {(['all', 'pending', 'active', 'suspended'] as DriverFilter[]).map(f => {
                const count = f === 'all' ? drivers.length : drivers.filter(d => driverStatus(d) === f).length;
                const active = driverFilter === f;
                const accentColor = f === 'pending' ? '#f59e0b' : f === 'active' ? '#22c55e' : f === 'suspended' ? '#f97316' : '#1a73e8';
                return (
                  <button key={f} onClick={() => setDriverFilter(f)} style={{
                    padding: '6px 14px', border: `1.5px solid ${active ? accentColor : '#e5e7eb'}`,
                    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                    backgroundColor: active ? accentColor : '#fff',
                    color: active ? '#fff' : '#374151',
                  }}>
                    {f.charAt(0).toUpperCase() + f.slice(1)} ({count})
                  </button>
                );
              })}
            </div>

            <table style={styles.table}>
              <thead>
                <tr><th>Name</th><th>Phone</th><th>Online</th><th>Status</th><th>Rating</th><th>Car Type</th><th>License No.</th><th>Signed Up</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredDrivers.map(d => (
                  <tr key={d._id} style={clickableRow} onMouseEnter={hoverRow} onMouseLeave={unhoverRow} onClick={() => setProfileUser(d)}>
                    <td>{d.name}</td>
                    <td>{d.phone}</td>
                    <td>{d.isOnline ? '🟢 Online' : '🔴 Offline'}</td>
                    <td>{driverStatusBadge(d)}</td>
                    <td>{starRating(d.rating)}</td>
                    <td style={{ fontSize: 13 }}>{(d as any).vehicleType || 'N/A'}</td>
                    <td style={{ fontSize: 13, fontFamily: 'monospace' }}>{(d as any).licenseNumber || 'N/A'}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatTs(d.createdAt)}</td>
                    {driverActionCell(d)}
                  </tr>
                ))}
                {filteredDrivers.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                    No {driverFilter === 'all' ? '' : driverFilter + ' '}drivers.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* RIDERS */}
        {tab === 'riders' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>👤 Riders</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={() => setAddModalRole('rider')}>
                + Add New Rider
              </button>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Account</th><th>Total Rides</th><th>Rating</th><th>Signed Up</th><th>Actions</th></tr></thead>
              <tbody>
                {riders.map(r => (
                  <tr key={r._id} style={clickableRow} onMouseEnter={hoverRow} onMouseLeave={unhoverRow} onClick={() => setProfileUser(r)}>
                    <td>{r.name}</td>
                    <td>{r.phone}</td>
                    <td>{r.email}</td>
                    <td>{statusBadge(r.isActive)}</td>
                    <td>{r.totalRides || 0}</td>
                    <td>{starRating(r.rating)}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{formatTs(r.createdAt)}</td>
                    {userActionCell(r)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* DISPATCHERS */}
        {tab === 'dispatchers' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>👨‍💼 Dispatchers</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={() => setAddModalRole('dispatcher')}>
                + Add Dispatcher
              </button>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Account</th><th>Actions</th></tr></thead>
              <tbody>
                {dispatchers.map(d => (
                  <tr key={d._id} style={clickableRow} onMouseEnter={hoverRow} onMouseLeave={unhoverRow} onClick={() => setProfileUser(d)}>
                    <td>{d.name}</td><td>{d.phone}</td><td>{d.email || '–'}</td>
                    <td>{statusBadge(d.isActive)}</td>
                    {userActionCell(d)}
                  </tr>
                ))}
                {dispatchers.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No dispatchers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* DISPATCH BOARD */}
        {tab === 'dispatch-board' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>📋 Dispatch Board</h2>
              <span style={{ fontSize: 13, color: '#9ca3af' }}>
                {boardRides.length} active ride{boardRides.length !== 1 ? 's' : ''} · {availableDrivers.length} drivers available
              </span>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Rider</th><th>Route</th><th>Status</th><th>Driver</th><th>Assign</th><th>Contact</th></tr></thead>
              <tbody>
                {boardRides.map(r => {
                  const needsDriver = !r.driver || r.status === 'pending' || r.status === 'searching';
                  return (
                    <tr key={r._id}>
                      <td>{r.rider?.name || 'N/A'}</td>
                      <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 200 }}>
                        {r.pickup?.address || '–'} → {r.destination?.address || r.dropoff?.address || '–'}
                      </td>
                      <td><span style={{ ...styles.badge, backgroundColor: needsDriver ? '#ff9800' : '#3b82f6' }}>{r.status}</span></td>
                      <td>
                        {needsDriver ? (
                          <select style={styles.select} value={rideDriverMap[r._id] || ''}
                            onChange={e => setRideDriverMap(prev => ({ ...prev, [r._id]: e.target.value }))}>
                            <option value="">Select driver…</option>
                            {availableDrivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                          </select>
                        ) : <span style={{ fontWeight: 500 }}>{r.driver?.name}</span>}
                      </td>
                      <td>
                        {needsDriver && (
                          <button style={{ ...styles.actionBtn, backgroundColor: rideDriverMap[r._id] ? '#43a047' : '#d1d5db', fontSize: 12 }}
                            disabled={!rideDriverMap[r._id]} onClick={() => assignDriver(r._id)}>Assign</button>
                        )}
                      </td>
                      <td>
                        <button style={{ ...styles.actionBtn, backgroundColor: '#6366f1', fontSize: 12 }} onClick={() => openContact(r)}>Contact</button>
                      </td>
                    </tr>
                  );
                })}
                {boardRides.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No active rides.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* DISPATCH LOG */}
        {tab === 'dispatch-log' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>📝 Dispatch Activity Log</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#6366f1' }} onClick={reloadDispatchLog}>Refresh</button>
            </div>
            <table style={styles.table}>
              <thead><tr><th>Dispatcher</th><th>Ride ID</th><th>Driver Assigned</th><th>Time</th></tr></thead>
              <tbody>
                {dispatchLog.map((entry, i) => (
                  <tr key={entry._id || i}>
                    <td>{entry.dispatcher?.name || entry.dispatcherName || '–'}</td>
                    <td style={{ fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>{entry.rideId || entry.ride?._id || entry.ride || '–'}</td>
                    <td>{entry.driver?.name || entry.driverName || '–'}</td>
                    <td style={{ fontSize: 13, color: '#6b7280' }}>{formatTs(entry.createdAt)}</td>
                  </tr>
                ))}
                {dispatchLog.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No activity yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'ai'           && <AIAssistant token={token} />}
        {tab === 'live-map'     && <LiveMap />}
        {tab === 'settings'     && <Settings token={token} />}
        {tab === 'create-admin' && <CreateAdmin token={token} />}
      </div>

      {/* ── Modals ── */}
      {addModalRole && <AddUserModal role={addModalRole} token={token} onClose={() => setAddModalRole(null)} onDone={reloadUsers} />}
      {profileUser  && <ProfileModal user={profileUser} onClose={() => setProfileUser(null)} />}
      {contactModal && <ContactModal title={contactModal.title} contacts={contactModal.contacts} onClose={() => setContactModal(null)} />}
      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          confirmColor={pendingConfirm.confirmColor}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── App root ──────────────────────────────────────────────────────────────────

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const handleLogin  = (t: string) => { localStorage.setItem('adminToken', t); setToken(t); };
  const handleLogout = ()          => { localStorage.removeItem('adminToken'); setToken(''); };
  return token ? <Dashboard token={token} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />;
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles: any = {
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e' },
  loginBox:       { backgroundColor: '#fff', padding: 40, borderRadius: 12, width: 380, textAlign: 'center' as const },
  logo:           { fontSize: 24, marginBottom: 8 },
  loginTitle:     { color: '#333', marginBottom: 24 },
  input:          { width: '100%', padding: 12, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' as const },
  button:         { width: '100%', padding: 14, backgroundColor: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' },
  error:          { color: 'red', marginBottom: 12 },
  container:      { display: 'flex', height: '100vh', fontFamily: 'sans-serif' },
  sidebar:        { width: 220, backgroundColor: '#1a1a2e', color: '#fff', padding: 20, display: 'flex', flexDirection: 'column' as const, overflowY: 'auto' as const },
  sidebarTitle:   { fontSize: 16, marginBottom: 24 },
  navItem:        { padding: '12px 16px', cursor: 'pointer', borderRadius: 8, marginBottom: 4, fontSize: 14 },
  navActive:      { backgroundColor: '#1a73e8' },
  navBadge:       { position: 'absolute' as const, top: 6, right: 8, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 5px', minWidth: 16, textAlign: 'center' as const },
  main:           { flex: 1, padding: 30, backgroundColor: '#f5f5f5', overflowY: 'auto' as const },
  statsGrid:      { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 20 },
  statCard:       { backgroundColor: '#fff', padding: 24, borderRadius: 12, textAlign: 'center' as const, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table:          { width: '100%', borderCollapse: 'collapse' as const, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  badge:          { padding: '3px 10px', borderRadius: 20, color: '#fff', fontSize: 12, display: 'inline-block' },
  actionBtn:      { padding: '6px 14px', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: 13 },
  tabHeader:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  select:         { padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, backgroundColor: '#fff', cursor: 'pointer', minWidth: 150 },
  overlay:        { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox:       { background: '#fff', borderRadius: 14, padding: 28, width: 440, maxWidth: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  modalTitle:     { marginTop: 0, marginBottom: 18, color: '#111', fontSize: 18 },
  modalActions:   { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  formInput:      { display: 'block', width: '100%', padding: '10px 12px', marginBottom: 10, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' as const },
};
